import { usePagination } from 'hooks/usePagination';
import { useEffect, useState } from 'react';
import TablePagination from 'components/table/base/tablePagination';
import { useTranslation } from 'react-i18next';
import RefreshIcon from 'assets/icons/refresh.svg';
import useModifiers from 'hooks/useModifiers';
import TablePageSelector from './tablePageSelector';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum } from '../../tooltip';

interface PaginationItems {
  from: number;
  to: number;
  of: number | undefined;
}

export enum TableFooterModifiers {
  OVERVIEW = 'overview',
}

export interface TableFooterProps {
  isBottom: boolean;// TODO: We should split this into tableheader and tablefooter
  totalPageCount: number;
  totalItemsCount: number | undefined;
  cacheDateTime: string | null;
  pageSize: number;
  siblingCount: number;
  pageIndex: number;
  onPaginate: (v: number) => void;
  onPageSizeSelect: (v: number) => void;
  showFirstLastButton?: boolean;
  refreshCacheAction: () => void;
  isLoading: boolean;
  modifiers: TableFooterModifiers[];
}

function TableFooter(props: TableFooterProps) {
  const [paginationItems, setPaginationItems] = useState<PaginationItems | undefined>(undefined);
  const { t } = useTranslation();
  let waitingForResponse = false;
  const mods = useModifiers(props.modifiers);

  const {
    totalPageCount,
    totalItemsCount,
    cacheDateTime,
    pageSize,
    pageIndex,
    siblingCount,
    onPaginate,
    onPageSizeSelect,
    refreshCacheAction,
    isLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showFirstLastButton,
  } = props;

  const paginationRange = usePagination({
    pageIndex,
    totalPageCount,
    siblingCount,
    pageSize,
  });

  useEffect(() => {
    if (totalItemsCount) {
      const isLastPage = (pageIndex + 1) === totalPageCount;

      const currentPaginationItems: PaginationItems = {
        from: pageSize * pageIndex + 1,
        to: isLastPage ? totalItemsCount : (pageIndex + 1) * pageSize,
        of: totalItemsCount,
      };

      setPaginationItems(currentPaginationItems);
    }
  }, [pageIndex, totalItemsCount, totalPageCount]);

  const onNext = () => {
    onPaginate(pageIndex + 1);
  };

  const onPrevious = () => {
    onPaginate(pageIndex - 1);
  };

  const handleRefreshCacheClick = () : void => {
    waitingForResponse = true;
    refreshCacheAction();
  };

  return (
    totalItemsCount || process.env.NODE_ENV === 'development' ? (
      // _isBottom -> Alignment hack. We should remove this when possible
      <div className={`ct_tableFooter ${mods} ${!props.isBottom ? '_isBottom' : ''}`}>
        <div>
          <input type="hidden" name="filter[forceRefreshCache]" value="0" />
          {((cacheDateTime === null || isLoading) && process.env.NODE_ENV !== 'development') ? null : (
            <div className="ct_tableFooter-cached">
              <Tooltip
                align={TooltipAlignEnum.LEFT}
                title={t('Refresh now', { ns: 'overview' })}
                message={t('Force a refresh of the cached data and reload the table', { ns: 'overview' })}
              >
                <div className="ct_tableFooter-cached-forceRefreshButton" onClick={handleRefreshCacheClick}>
                  <RefreshIcon name="refreshIcon" />
                </div>
              </Tooltip>
              <span className="ct_tableFooter-cached-text">
                <Tooltip
                  align={TooltipAlignEnum.LEFT}
                  cursor={TooltipCursorEnum.HELP}
                  message={
                      t(
                        'Cached data will be shown for 24 hours for performance purposes, '
                        + 'but you can trigger a manual refresh on the left',
                        { ns: 'overview' },
                      )
                    }
                >
                  {cacheDateTime === null ? (
                    <span><i>Refresh button is always shown in development environment</i></span>
                  ) : (
                    <span>
                      Cached
                      {' '}
                      data
                      {' '}
                      from
                      {' '}
                      {cacheDateTime}
                    </span>
                  )}
                </Tooltip>
              </span>
            </div>
          )}
        </div>
        { props.isBottom && (
        <>
          <div className="ct_tableFooter-main">
            <TablePagination
              passed={props}
              paginationRange={paginationRange}
              onNext={() => onNext()}
              onPrevious={() => onPrevious()}
            />
          </div>

          { totalItemsCount > 1 ? (
            <div className="ct_tableFooter-aside">
              <TablePageSelector pageSize={pageSize} onPageSizeSelect={onPageSizeSelect} />
              {!!paginationItems && (
              <div className="ct_tableFooter-details">
                {t('Showing {{from}} to {{to}} of {{entries}} entries', { from: paginationItems.from, to: paginationItems.to, entries: paginationItems.of as number })}
              </div>
              )}
            </div>
          ) : ''}
        </>
        )}
      </div>
    ) : null
  );
}

TableFooter.defaultProps = { ...{} };

export default TableFooter;
